import { put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import _ from 'lodash';
import { URL, API_DOMAIN, PLATFORM, PLATFORM_VERSION, REMOTE_CONFIG_KEYS } from '../config/Constants';
import Utility from '../utils/Utility';
import {
  FETCH_ROUTINE_DETAILS,
  FETCH_ROUTINE_SECTION_LISTS,
  ROUTINE_ACTIVITY_DONE,
  FETCH_POLLS_DATA,
  SELECT_POLL_OPTION,
  FETCH_ROUTINE_LIST_FOR_URL,
  FETCH_SKIN_HAPPINESS_DATA,
} from '../actions/RoutineActions';

import { convertJsonFromResponse, getApiHeaders } from './GeneratorUtil';
import { ROUTINE_APPLICATION, CREATE_ROUTINE_PARTICIPATION, routineParticipations, FETCH_ROUTINE_APPLICATION_STATUS, RATE_EXPERIENCE} from '../actions/ActionTypes';
import { isPresent, isBlank } from '../utils/BooleanUtility';
import { filteredRequiredAttribute } from '../utils/UriUtility';
import { cacheData, clearAllCacheData, clearIndividualCache, getCachedData } from '../utils/cacheUtils';

export function* getRoutineDetails(action) {
  const { callback, slug, clearCache = false, shouldAppendAttributes = false, shouldCacheData = false } = action;
  let url = `${URL.ROUTINE_URL}/${slug}`;
  const isPersonalizedUrl = slug.includes('personalized') || shouldAppendAttributes;
  if (isPersonalizedUrl) {
    url = `${url}?`;
    url = filteredRequiredAttribute(url, [], isPersonalizedUrl);
  }
  const cachedData = getCachedData(url, 'routines');

  if (clearCache) {
    clearIndividualCache(url);
  } else if (shouldCacheData && isPresent(cachedData)) {
    const response = cachedData;
    callback(true, response);
    return;
  }

  try {
    const response = yield fetch(url, {
      method: 'GET',
      headers: yield getApiHeaders(),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, {});
        return;
      }
      if (isBlank(cachedData) && shouldCacheData) {
        cacheData(url, json, 'routines');
      }
      callback(true, json);
    } else {
      callback(false, {});
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* getRoutineLists(action) {
  const { callback, slug } = action;
  try {
    const response = yield fetch(`${URL.ROUTINE_LIST}/${slug}/routine_lists`, {
      method: 'GET',
      headers: yield getApiHeaders(),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, {});
        return;
      }
      callback(true, json);
    } else {
      callback(false, {});
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* routineActivityMarkAsDone(action) {
  const { callback, routineActivityId = '', routineSlug = '' } = action;
  try {
    const response = yield fetch(`${URL.ROUTINE_ACTIVITY_URL}/${routineSlug}/users_routine_activities`, {
      method: 'POST',
      headers: yield getApiHeaders(),
      body: JSON.stringify({
        routine_activity_id: routineActivityId,
      }),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, {});
        return;
      }
      callback(true, json);
    } else {
      callback(false, json);
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* fetchPollActionCardData(action) {
  const { callback, id = '' } = action;
  try {
    const response = yield fetch(`${URL.POLL_LIST}/${id}`, {
      method: 'GET',
      headers: yield getApiHeaders(),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, {});
        return;
      }
      callback(true, json);
    } else {
      callback(false, {});
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* selectPollOption(action) {
  const { callback, questionId = '', optionIds = [], pollId = '' } = action;
  try {
    const response = yield fetch(`${URL.POLL_LIST}/${pollId}/poll_questions/${questionId}/poll_answers`, {
      method: 'POST',
      headers: yield getApiHeaders(),
      body: JSON.stringify({
        poll_question_option_ids: optionIds,
      }),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, {});
        return;
      }
      callback(true, json);
    } else {
      callback(false, {});
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* createRoutineParticipationAsync(action) {
  const { callback = () => {}, data = {}, uri = '' } = action;

  const url = isPresent(uri) ? uri : `${URL.CREATE_ROUTINE_PARTICIPATION}`;
  try {
    const response = yield fetch(url, {
      method: 'POST',
      headers: yield getApiHeaders(),
      body: JSON.stringify(data),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, json);
        return;
      }
      yield put(routineParticipations(data.routine_participation.routine_slug));
      callback(true, json);
    } else {
      callback(false, json);
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* fetchRoutineListForURL(action) {
  const { callback = () => {}, url = '' } = action;
  try {
    const response = yield fetch(`${url.trim()}`, {
      method: 'GET',
      headers: yield getApiHeaders(),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, {});
        return;
      }
      callback(true, json);
    } else {
      callback(false, {});
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* routineApplication(action) {
  const { callback, data } = action;
  try {
    const response = yield fetch(`${API_DOMAIN}/api/v1/routine_applications`, {
      method: 'POST',
      headers: yield getApiHeaders(),
      body: JSON.stringify(data),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, {});
        return;
      }
      callback(true, json);
    } else {
      callback(false, {});
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* fetchRoutineApplicationStatus(action) {
  const { callback, id = '' } = action;
  try {
    const response = yield fetch(`${API_DOMAIN}/api/v1/routine_applications?routine_id=${id}`, {
      method: 'GET',
      headers: yield getApiHeaders(),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (Utility.isBlank(json)) {
        callback(false, {});
        return;
      }
      callback(true, json);
    } else {
      callback(false, {});
    }
  } catch (error) {
    callback(false, {});
  }
}

export function* fetchSkinHappinessData(action) {
  const { id, skinScore, callback } = action;
  try {
    const response = yield fetch(`${URL.HAPPINESS_UNIT}`, {
      method: 'POST',
      headers: yield getApiHeaders(),
      body: JSON.stringify({
        score: skinScore,
        routine_day_id: id,
      }),
    });
    const statusCode = response.status;
    const json = yield convertJsonFromResponse(response);
    if (statusCode >= 200 && statusCode < 300) {
      if (isBlank(json)) {
        callback(false, {});
        return;
      }
      callback(true, json);
    } else {
      callback(false, {});
    }
  } catch (error) {
    callback(false, {});
  }
}

function* rateExperience(action) {
  const { body, callback } = action;
  const { entityType, entityId, rating } = body;

  try {
    const response = yield fetch(URL.RATE_EXPERIENCE_URL, {
      method: 'POST',
      headers: yield getApiHeaders(),
      body: JSON.stringify({
        rating: {
          star: rating,
          entity_type: entityType,
          entity_id: entityId,
        },
      }),
    });

    if (response.status >= 200 && response.status < 300) {
      callback('success');
    } else {
      const json = yield convertJsonFromResponse(response);
      if (!Utility.isBlank(json)) {
        callback(json.errors[0]);
      }
    }
  } catch (error) {
    console.tron.log(`Error: ${JSON.stringify(error)} URL is`);
  }
}


export default function* watchRoutineSaga() {
  yield takeLatest(FETCH_ROUTINE_DETAILS, getRoutineDetails);
  yield takeLatest(FETCH_ROUTINE_SECTION_LISTS, getRoutineLists);
  yield takeLatest(ROUTINE_ACTIVITY_DONE, routineActivityMarkAsDone);
  yield takeEvery(FETCH_POLLS_DATA, fetchPollActionCardData);
  yield takeLatest(SELECT_POLL_OPTION, selectPollOption);
  yield takeLatest(CREATE_ROUTINE_PARTICIPATION, createRoutineParticipationAsync);
  yield takeLatest(FETCH_ROUTINE_LIST_FOR_URL, fetchRoutineListForURL);
  yield takeLatest(ROUTINE_APPLICATION, routineApplication);
  yield takeEvery(FETCH_SKIN_HAPPINESS_DATA, fetchSkinHappinessData);
  yield takeEvery(FETCH_ROUTINE_LIST_FOR_URL, fetchRoutineListForURL);
  yield takeEvery(FETCH_ROUTINE_APPLICATION_STATUS, fetchRoutineApplicationStatus);
  yield takeLatest(RATE_EXPERIENCE, rateExperience);
}
